import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Input,
  OnInit,
  ViewChild,
} from '@angular/core';
import {
  buildPageRenderedTrackingAction,
  I2gNgTrackingService,
  ITrackingAction,
} from 'i2g-ng-tracking';
import { I2gDialogService } from 'i2g-ng-ui';
import { LocalizationService, WindowRefService } from 'i2g-ng-utils';
import { from, fromEvent } from 'rxjs';
import {
  defaultIfEmpty,
  filter,
  switchMap,
  take,
  withLatestFrom,
} from 'rxjs/operators';
import { environment } from '../../../../../environments/environment';
import { EnvironmentsEnum } from '../../../../../environments/environments.enum';
import { FeaturesDataStore } from '../../../features-data/features-data.store';
import { buildOptOutTrialCanvasTrackingObject } from '../../../tracking/tracking-object.builder';

const originS3 = ['https://i.2go.com', 'https://i.staging.invoice2go.io'];
if (environment.env !== EnvironmentsEnum.production) {
  originS3.push('https://localhost:4200');
}

@Component({
  selector: 'app-opt-out-trial-iframe-dialog',
  templateUrl: './pmr-dialog.component.html',
  styleUrls: ['./pmr-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    I2gNgTrackingService.withObject(buildOptOutTrialCanvasTrackingObject()),
  ],
})
export class PmrDialogComponent implements OnInit {
  @Input() deeplinkParams: any;

  @ViewChild('iframe', { static: true }) iframe: ElementRef;

  constructor(
    private _windowRefService: WindowRefService,
    private _featuresDataStore: FeaturesDataStore,
    private _localizationService: LocalizationService,
    private _trackingService: I2gNgTrackingService,
    private _dialogService: I2gDialogService
  ) {}

  ngOnInit() {
    const iframe = this.iframe.nativeElement;
    const canvasName = this.deeplinkParams.canvas;
    iframe.src = this.deeplinkParams.url;

    const pageLoadStartTime = Date.now();

    const optOutTrialCanvas$ = this._featuresDataStore.canvases$.pipe(
      switchMap((canvases) => from(canvases.toJS())),
      filter((canvas: any) => canvas.name === canvasName),
      defaultIfEmpty(null)
    );

    const iframeLoaded$ = fromEvent(iframe, 'load');

    iframeLoaded$
      .pipe(withLatestFrom(optOutTrialCanvas$), take(1))
      .subscribe(([_, canvas]) => {
        try {
          iframe.contentWindow.postMessage(
            JSON.stringify({
              canvas,
              locale: this._localizationService.getCurrentLocale(),
            }),
            iframe.src
          );

          this._windowRefService.nativeWindow.addEventListener(
            'message',
            this.messageHandler.bind(this),
            false
          );

          this._trackPageRendered(pageLoadStartTime, true);
        } catch (error) {
          this._trackPageRendered(pageLoadStartTime, false, error);
        }
      });
  }

  private _trackPageRendered(
    pageLoadStartTime: number,
    success: boolean,
    error: string = ''
  ) {
    const loadTime = Date.now() - pageLoadStartTime;
    const trackingAction = buildPageRenderedTrackingAction(
      loadTime,
      0, // iframe creation time - not worth tracking on the web
      success,
      error
    );
    this._trackingService.trackEvent(null, trackingAction);
  }

  messageHandler(message: MessageEvent) {
    const iframe = this.iframe.nativeElement;
    const { origin, source } = message;

    if (source === iframe.contentWindow && originS3.includes(origin)) {
      const { action, payload } = JSON.parse(message.data);
      if (action === 'track') {
        const trackingAction = <ITrackingAction>{ name: payload.action };
        this._trackingService.trackEvent(null, trackingAction, payload.props);
      } else if (action === 'navigate') {
        this._dialogService.closeDialog(payload);
      }
    }
  }
}
